/**
 * Wedding Books — the portfolio grid.
 *
 * Every book on the platform, filterable by status and tier, searchable across
 * wedding code / partner names / couple email, sortable on the columns the
 * server can actually order by. Row click opens the detail page.
 *
 * SERVER-SIDE EVERYTHING. Paging, filtering and sorting all happen in SQL — the
 * grid is a view over a query, not a client-side table with 10,000 rows in
 * memory. The sort field is whitelisted server-side; anything else silently
 * falls back to created_at rather than erroring.
 *
 * ⚠️ "AT RISK" IS DEFINED ONCE, ON THE SERVER, AND THIS PAGE NEVER RECOMPUTES
 * IT. It is three surfaces of one fact — a row flag, a filter value, and a KPI
 * count — and the moment the grid derived its own version those three could
 * disagree about which books are in trouble. The definition (`at_risk_definition`)
 * arrives in the payload and is shown in the KPI card's tooltip, so an operator
 * can read the rule rather than infer it.
 *
 * The search box is debounced so a typed word is one query, not seven.
 *
 * THE FILTERS LIVE IN THE URL (`?status=&tier=&q=`), via `useUrlParam`. That is
 * what makes `/dashboard/wedding/books?status=at_risk` a link: clicking the
 * at-risk tile and pasting the address bar into a message reproduces the click
 * for whoever opens it, and a refresh keeps the filter. Page and sort stay
 * local on purpose — "which page of the grid I was on" is not a fact worth
 * putting in a shared link, and a sort is a reading preference, not a dataset.
 */

import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Chip,
  Grid,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/FileDownload';
import { AutoStories as BooksIcon } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridPaginationModel,
} from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import EmptyState from '../../common/EmptyState';
import useUrlParam from '../../common/useUrlParam';
import { csvUrl, startCsvDownload } from '../csvDownload';
import WeddingKpiCard from '../WeddingKpiCard';
import { WeddingStatusChip, WeddingTierChip } from '../WeddingChips';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  AtRiskDefinition, BookRow, BooksKpis, BooksResponse, BooksStatusCounts,
} from '../weddingApi';
import {
  BOOK_STATUSES,
  STATUS_META,
  TIERS,
  TIER_META,
  centsToUsd,
  centsToUsdTile,
  num,
  shortDate,
  deadlineCountdown,
} from '../weddingFormat';

/** Columns the server will actually order by (weddingAdmin.js BOOK_SORT_COLUMNS). */
const SORTABLE = new Set([
  'created_at', 'wedding_date', 'contribution_deadline',
  'status', 'tier', 'revenue_cents', 'submitted_guests',
  'invited_guests', 'total_guests', 'photos', 'invites_sent', 'participation',
  'reminders_due', 'guests_at_reminder_ceiling',
]);

/**
 * The four pills, in lifecycle order. `at_risk` is a PSEUDO-STATUS: it is not a
 * value of wedding_books.status, it is a computed condition the server treats
 * as a peer of the real ones — which is why it sits in the same control.
 */
/**
 * Everything `?status=` may hold. `at_risk` is the pseudo-status the pills and
 * the select already treat as a peer of the real ones, so it belongs in the
 * same allowlist — without it a shared at-risk link would silently widen to
 * the whole portfolio.
 */
const STATUS_PARAM_VALUES: readonly string[] = [...BOOK_STATUSES, 'at_risk'];

const PILLS: { value: string; label: string; key: keyof BooksStatusCounts }[] = [
  { value: '', label: 'All', key: 'all' },
  { value: 'collecting', label: 'Collecting', key: 'collecting' },
  { value: 'at_risk', label: 'At risk', key: 'at_risk' },
  { value: 'closed', label: 'Closed', key: 'closed' },
];

function atRiskSentence(def: AtRiskDefinition | undefined): string {
  if (!def) return 'Low participation with a deadline approaching.';
  return `Still ${def.statuses.join(' or ')}, deadline within ${def.deadline_within_days} days, `
    + `at least one guest invited, and under ${Math.round(def.participation_below * 100)}% of them `
    + 'have submitted.';
}

const WeddingBooksPage: React.FC = () => {
  const navigate = useNavigate();

  const [rows, setRows] = React.useState<BookRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [counts, setCounts] = React.useState<BooksStatusCounts | null>(null);
  const [kpis, setKpis] = React.useState<BooksKpis | null>(null);
  const [atRiskDefinition, setAtRiskDefinition] = React.useState<AtRiskDefinition | undefined>();
  // The server's own description of what "due" counts. Rendered, not
  // paraphrased — the word it insists on is "scheduled", never "will send".
  const [remindersDueDefinition, setRemindersDueDefinition] =
    React.useState<Record<string, unknown> | undefined>();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [status, setStatus] = useUrlParam('status', { allowed: STATUS_PARAM_VALUES });
  const [tier, setTier] = useUrlParam('tier', { allowed: TIERS });
  const [search, setSearch] = useUrlParam('q');

  // The typed value, seeded from the URL so a pasted `?q=okafor` shows the word
  // it filtered by rather than an empty box over a filtered grid.
  const [searchInput, setSearchInput] = React.useState(search);

  const [pagination, setPagination] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ]);

  // Held in refs so the debounce below depends on the TYPED VALUE alone. The
  // setter's identity changes with every URL write, and the committed value
  // changes with every commit — either in the dependency list would restart the
  // timer on the hook's own output.
  const searchRef = React.useRef(search);
  searchRef.current = search;
  const setSearchRef = React.useRef(setSearch);
  setSearchRef.current = setSearch;

  // Debounce the search box: one query per typed word, not one per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = searchInput.trim();
      // Mount re-commits the value it was seeded with; writing it would replace
      // the history entry for no change at all.
      if (trimmed === searchRef.current) return;
      setSearchRef.current(trimmed);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  /**
   * The sort the SERVER will honour, resolved once and used by both the grid
   * query and the CSV link — so the export cannot be ordered differently from
   * the rows the operator is looking at.
   */
  const sortQuery = React.useMemo((): { sortBy?: string; sortOrder?: 'ASC' | 'DESC' } => {
    const sort = sortModel[0];
    if (!sort || !SORTABLE.has(sort.field)) return {};
    return { sortBy: sort.field, sortOrder: sort.sort === 'asc' ? 'ASC' : 'DESC' };
  }, [sortModel]);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res: BooksResponse = await weddingApi.getBooks({
        page: pagination.page + 1,
        limit: pagination.pageSize,
        ...(status ? { status } : {}),
        ...(tier ? { tier } : {}),
        ...(search ? { search } : {}),
        ...sortQuery,
      });
      setRows(res.books || []);
      setTotal(res.pagination?.total || 0);
      setCounts(res.status_counts || null);
      setKpis(res.kpis || null);
      setAtRiskDefinition(res.at_risk_definition);
      setRemindersDueDefinition(res.reminders_due_definition);
    } catch (e) {
      setError(e instanceof WeddingApiError ? e.message : 'Could not load wedding books.');
      setRows([]);
      setTotal(0);
      setCounts(null);
      setKpis(null);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, status, tier, search, sortQuery]);

  React.useEffect(() => {
    load();
  }, [load]);

  const applyStatus = React.useCallback((next: string) => {
    setStatus(next);
    setPagination((p) => ({ ...p, page: 0 }));
  }, [setStatus]);

  /**
   * THE CSV IS THE GRID. Same filters, same order, no page — the API's param is
   * `search` even though the URL key is the shorter `q`, and the two are wired
   * here so they cannot drift.
   */
  const exportHref = csvUrl('/books/export.csv', {
    ...(status ? { status } : {}),
    ...(tier ? { tier } : {}),
    ...(search ? { search } : {}),
    ...sortQuery,
  });

  /** 0.58 → "58%". NULL means the book has invited nobody: no denominator, no rate. */
  const participationOf = (row: BookRow): number | null => {
    if (row.participation !== undefined && row.participation !== null) return row.participation;
    const invited = num(row.invited_guests);
    if (invited <= 0) return null;
    return num(row.submitted_guests) / invited;
  };

  /**
   * EVERY column here uses `renderCell`, so EVERY column needs `display: 'flex'`.
   *
   * x-data-grid v8 changed the default cell box from a centred flex container to
   * `display: block` and calls the new mode 'text'. In 'text' mode a cell that
   * renders elements — a Chip, a Tooltip, a two-line Box — is laid out as inline
   * content in a 52px row it does not know about: chips sit off the text
   * baseline and a two-line cell spills over the row divider into the row below.
   * `display: 'flex'` is the documented opt-back-in and it is not optional for a
   * grid built out of components.
   *
   * jsdom has no layout, so the component tests could not have caught this.
   * It was caught the first time the page was opened in a browser (session I).
   */
  const columns: GridColDef<BookRow>[] = [
    {
      field: 'wedding_code',
      headerName: 'Code',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow, string>) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'couple',
      headerName: 'Couple',
      flex: 1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      valueGetter: (_value: unknown, row: BookRow) => `${row.partner1_name} & ${row.partner2_name}`,
      renderCell: (params: GridRenderCellParams<BookRow>) => (
        <Box sx={{ overflow: 'hidden' }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {params.row.partner1_name} &amp; {params.row.partner2_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {params.row.couple_email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tier',
      headerName: 'Tier',
      width: 110,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => <WeddingTierChip tier={params.row.tier} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 170,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => (
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <WeddingStatusChip status={params.row.status} />
          {params.row.at_risk && (
            <Tooltip title={atRiskSentence(atRiskDefinition)}>
              <Chip size="small" color="error" variant="filled" label="At risk" />
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      field: 'participation',
      headerName: 'Participation',
      width: 160,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => {
        const fraction = participationOf(params.row);
        const submitted = num(params.row.submitted_guests);
        const invited = num(params.row.invited_guests);
        if (fraction === null) {
          return (
            <Tooltip title="Nobody has been invited yet, so there is no participation rate to show.">
              <Typography variant="body2" color="text.secondary">—</Typography>
            </Tooltip>
          );
        }
        return (
          <Box sx={{ width: '100%' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.round(fraction * 100))}
              color={params.row.at_risk ? 'error' : 'success'}
              sx={{ height: 7, borderRadius: 4 }}
              aria-label={`${Math.round(fraction * 100)}% participation`}
            />
            <Typography variant="caption" color="text.secondary">
              {submitted} of {invited}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'submitted_guests',
      headerName: 'Stories',
      width: 110,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => {
        const submitted = num(params.row.submitted_guests);
        const invited = num(params.row.invited_guests);
        return (
          <Tooltip title={`${submitted} submitted of ${invited} invited (${num(params.row.total_guests)} on the list)`}>
            <Typography variant="body2">
              {submitted} / {invited}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'photos',
      headerName: 'Photos',
      width: 100,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => (
        <Typography variant="body2">
          {params.row.photos === undefined ? '—' : num(params.row.photos).toLocaleString()}
        </Typography>
      ),
    },
    {
      /**
       * SCHEDULED, NOT "WILL SEND" — and the difference is the whole point.
       *
       * These are guests the reminder engine currently has queued. The engine
       * re-checks at send time and a guest who submits in the meantime is
       * dropped, so this is an upper bound on messages, not a forecast of them.
       * The server ships `reminders_due_definition` saying exactly that, and
       * the tooltip repeats its own sentence rather than a paraphrase.
       *
       * Added because the CSV already carried this column. An export with
       * columns the grid does not show is the same failure as a grid with
       * numbers the export omits: two artefacts from one screen that disagree.
       */
      field: 'reminders_due',
      headerName: 'Reminders due',
      width: 145,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => {
        const due = params.row.reminders_due;
        if (due === undefined) return <Typography variant="body2">—</Typography>;
        const ceiling = num(params.row.guests_at_reminder_ceiling);
        const max = Number(
          (remindersDueDefinition && remindersDueDefinition.max_reminders) || 3
        );
        const title = [
          `${num(due).toLocaleString()} guest(s) currently have a reminder scheduled for this book.`,
          'Scheduled is not sent: the engine re-checks at send time, and a guest who submits before'
          + ' then is dropped from the queue.',
          ceiling > 0
            ? `${ceiling.toLocaleString()} more have already had all ${max} reminders — that is as many as we send.`
            : null,
        ].filter(Boolean).join(' ');
        return (
          <Tooltip title={title}>
            <Typography
              variant="body2"
              sx={{ color: ceiling > 0 ? 'warning.main' : 'text.primary' }}
            >
              {num(due).toLocaleString()}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'invites_sent',
      headerName: 'Invites sent',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => {
        if (params.row.invites_sent === undefined) {
          return <Typography variant="body2">—</Typography>;
        }
        return (
          <Tooltip
            title={
              params.row.last_invite_at
                ? `Last sent ${shortDate(params.row.last_invite_at)}`
                : 'No invitation has been sent yet'
            }
          >
            <Typography variant="body2">{num(params.row.invites_sent).toLocaleString()}</Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'contribution_deadline',
      headerName: 'Deadline',
      width: 150,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => {
        const countdown = deadlineCountdown(
          params.row.contribution_deadline,
          params.row.couple_timezone
        );
        return (
          <Tooltip title={shortDate(params.row.contribution_deadline)}>
            <Typography
              variant="body2"
              sx={{ color: countdown.overdue ? 'error.main' : 'text.primary' }}
            >
              {countdown.label}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'revenue_cents',
      headerName: 'Revenue',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {centsToUsd(params.row.revenue_cents)}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<BookRow>) => (
        <Typography variant="body2" color="text.secondary">
          {shortDate(params.row.created_at)}
        </Typography>
      ),
    },
  ];

  const medianPct =
    kpis && kpis.median_participation !== null && kpis.median_participation !== undefined
      ? `${Math.round(kpis.median_participation * 100)}%`
      : '—';

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <BooksIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Books
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {total} wedding book{total === 1 ? '' : 's'}. Open one to see its guests, purchases and
        activity, or to suspend it, move its deadline or nudge a contributor.
      </Typography>

      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="Median participation"
            value={medianPct}
            subtitle="of invited guests wrote something"
            showDelta={false}
            loading={loading}
            tooltip="The middle book, not the average — one 300-guest wedding cannot drag the number on its own. Books that have invited nobody have no rate and are left out."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          {/*
            CLICKABLE. The number and the filter are the same fact, so reading
            "4 at risk" and then having to find the filter is a step this page
            can just remove.
          */}
          <WeddingKpiCard
            title="Books at risk"
            value={kpis ? kpis.at_risk : '—'}
            subtitle="under 25% participation, under 14 days to the deadline"
            valueColor="error.main"
            showDelta={false}
            loading={loading}
            onClick={() => applyStatus('at_risk')}
            tooltip={atRiskSentence(atRiskDefinition)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="Closing this week"
            value={kpis ? kpis.closing_this_week : '—'}
            subtitle="deadline within 7 days"
            showDelta={false}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WeddingKpiCard
            title="Avg revenue / book"
            value={
              kpis && kpis.avg_revenue_cents !== null && kpis.avg_revenue_cents !== undefined
                ? centsToUsdTile(kpis.avg_revenue_cents)
                : '—'
            }
            subtitle="including upgrades and extras, across every book"
            showDelta={false}
            loading={loading}
            tooltip={
              kpis && kpis.avg_revenue_cents_paid_books
                ? `${centsToUsdTile(kpis.avg_revenue_cents_paid_books)} across books that have paid anything at all — drafts and abandoned checkouts drag the headline figure down.`
                : undefined
            }
          />
        </Grid>
      </Grid>

      {/*
        The pills double as counts, and the counts ignore the status filter on
        purpose (the server scopes them to tier + search only) — so a pill can
        never hide its own total the moment you select it.
      */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap role="group" aria-label="Status filter">
        {PILLS.map((pill) => {
          const active = status === pill.value;
          const count = counts ? counts[pill.key] : undefined;
          return (
            <Button
              key={pill.label}
              size="small"
              color={pill.value === 'at_risk' ? 'error' : 'primary'}
              variant={active ? 'contained' : 'outlined'}
              disableElevation
              aria-pressed={active}
              onClick={() => applyStatus(pill.value)}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              {`${pill.label} ${count === undefined ? '—' : count.toLocaleString()}`}
            </Button>
          );
        })}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="Code, partner name or couple email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => applyStatus(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {BOOK_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_META[s].label}
            </MenuItem>
          ))}
          {/* The pseudo-status, kept in the same control so the two cannot drift. */}
          <MenuItem value="at_risk">At risk (computed)</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Tier"
          value={tier}
          onChange={(e) => {
            setTier(e.target.value);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All tiers</MenuItem>
          {TIERS.map((t) => (
            <MenuItem key={t} value={t}>
              {TIER_META[t].label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => startCsvDownload(exportHref)}
          disabled={loading}
        >
          Export CSV
        </Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error ? (
        <EmptyState
          icon="error"
          title="Could not load the book list"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      ) : (
        <Box sx={{ height: 640, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            rowCount={total}
            paginationMode="server"
            sortingMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowClassName={(params) => (params.row.at_risk ? 'wedding-row--at-risk' : '')}
            onRowClick={(params) => navigate(`/dashboard/wedding/books/${params.row.id}`)}
            sx={{
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              // A warm tint derived from the theme's own warning colour — an
              // at-risk row should read as warm before it reads as red, and
              // alpha() keeps this a palette reference rather than a new hex.
              '& .wedding-row--at-risk': {
                backgroundColor: (t) => alpha(t.palette.warning.light, 0.14),
                '&:hover': { backgroundColor: (t) => alpha(t.palette.warning.light, 0.22) },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default WeddingBooksPage;
